import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { exportStyledExcel, QTY_FMT } from '../../utils/exportExcel';
import Pagination from '../../components/Pagination';

const GREEN = '#2e9f86';
const PAGE_SIZE = 50;

const DOC_TYPE_LABELS = {
  100: { label: 'Purchase In',     bg: '#eff6ff', color: '#1e40af' },
  120: { label: 'Purchase Return', bg: '#fff7ed', color: '#9a3412' },
  200: { label: 'Sales Out',       bg: '#f0fdf4', color: '#166534' },
  220: { label: 'Sales Return',    bg: '#f5f3ff', color: '#5b21b6' },
  230: { label: 'Proforma',        bg: '#f3f4f6', color: '#374151' },
  300: { label: 'Inventory Count', bg: '#f0f9ff', color: '#075985' },
  400: { label: 'Loss / Damage',   bg: '#fef2f2', color: '#991b1b' },
};

const pad = (x) => String(x).padStart(2, '0');
const fmtLocal = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

export default function StockMovements() {
  const { t } = useTranslation();

  const [movements,  setMovements]  = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [search,     setSearch]     = useState('');
  const [docType,    setDocType]    = useState('');
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');
  const [page,       setPage]       = useState(1);

  // products + variants for barcode → id resolution (an exact barcode in the search box
  // filters SERVER-side by product/variant, so it works across all pages, not just page 1)
  const { data: productsList = [] } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get('/api/products', { params: { all: true } }).then(r =>
      Array.isArray(r.data) ? r.data : (r.data?.data || [])
    ),
  });
  const { data: variantsList = [] } = useQuery({
    queryKey: ['variants-all'],
    queryFn: () => api.get('/api/variants').then(r => r.data).catch(() => []),
  });

  const barcodeMatch = useMemo(() => {
    const q = search.trim();
    if (!q || q.length < 4) return null; // avoid churn while typing short names
    const v = (variantsList || []).find(x => String(x.barcode || '') === q);
    if (v) return { variant_id: v._id };
    const p = (productsList || []).find(x => String(x.barcode || '') === q);
    if (p) return { product_id: p._id };
    return null;
  }, [search, productsList, variantsList]);

  const fetchMovements = useCallback(async (pg = page) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (docType)  params.set('document_type', docType);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to', dateTo);
      if (barcodeMatch?.variant_id)      params.set('variant_id', barcodeMatch.variant_id);
      else if (barcodeMatch?.product_id) params.set('product_id', barcodeMatch.product_id);
      params.set('page',  pg);
      params.set('limit', PAGE_SIZE);
      const res = await api.get(`/api/stock-movements?${params.toString()}`);
      const d = res.data;
      setMovements(d.data  || []);
      setTotal(d.total     || 0);
      setTotalPages(d.pages || 1);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [docType, dateFrom, dateTo, page, barcodeMatch]);

  useEffect(() => { fetchMovements(page); }, [fetchMovements]);

  // Exact barcode → already filtered server-side. Otherwise text search is
  // client-side on the current page only (server search not yet on this route).
  const filtered  = (search && !barcodeMatch)
    ? movements.filter(m => {
        const q = search.toLowerCase();
        return m.product_name?.toLowerCase().includes(q) ||
               m.variant_name?.toLowerCase().includes(q) ||
               m.document_number?.toLowerCase().includes(q) ||
               String(m.product_barcode || '').toLowerCase().includes(q) ||
               String(m.variant_barcode || '').toLowerCase().includes(q);
      })
    : movements;
  const paginated = filtered; // already paginated by server

  const clearFilters = () => { setSearch(''); setDocType(''); setDateFrom(''); setDateTo(''); setPage(1); };
  const hasFilters = search || docType || dateFrom || dateTo;

  const typeLabel = (code) => (DOC_TYPE_LABELS[code]?.label || String(code));

  // exports/prints ALL filtered rows (not just the current page)
  const handleExport = () => exportStyledExcel({
    filename: `stock_movements_${dateFrom || 'all'}_${dateTo || 'today'}`,
    sheetName: 'Stock Movements',
    title: 'Stock Movements',
    subtitle: `${dateFrom || dateTo ? `Period: ${dateFrom || '…'} — ${dateTo || 'today'} · ` : ''}${filtered.length} records`,
    columns: [
      { header: 'Date', key: 'date', width: 13 },
      { header: 'Product', key: 'product', width: 26 },
      { header: 'Variant', key: 'variant', width: 16 },
      { header: 'Type', key: 'type', width: 16 },
      { header: 'Document', key: 'doc', width: 14 },
      { header: 'Change', key: 'change', width: 10, numFmt: QTY_FMT, align: 'right' },
      { header: 'Before', key: 'before', width: 10, numFmt: QTY_FMT, align: 'right' },
      { header: 'After', key: 'after', width: 10, numFmt: QTY_FMT, align: 'right' },
    ],
    rows: filtered.map((m) => ({
      date: m.createdAt ? fmtLocal(m.createdAt) : '',
      product: m.product_name || '',
      variant: m.variant_name || '',
      type: typeLabel(m.document_type),
      doc: m.document_number || '',
      change: Number(m.quantity_change ?? 0),
      before: m.quantity_before ?? '',
      after: m.quantity_after ?? '',
    })),
  });

  const handlePrint = () => {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const rows = filtered.map((m) => {
      const ch = m.quantity_change ?? 0;
      return `<tr><td>${m.createdAt ? fmtLocal(m.createdAt) : '—'}</td><td>${esc(m.product_name)}${m.variant_name ? ' — ' + esc(m.variant_name) : ''}</td><td>${esc(typeLabel(m.document_type))}</td><td>${esc(m.document_number || '—')}</td><td class="r" style="color:${ch > 0 ? '#16a34a' : ch < 0 ? '#dc2626' : '#6b7280'}">${ch > 0 ? '+' : ''}${ch}</td><td class="r">${m.quantity_before ?? '—'}</td><td class="r">${m.quantity_after ?? '—'}</td></tr>`;
    }).join('');
    const win = window.open('', '_blank');
    if (!win) { alert('Pop-up blocked — allow pop-ups to print.'); return; }
    win.document.write(`<html><head><title>Stock Movements</title><style>body{font-family:Arial,sans-serif;font-size:12px;padding:24px;}h2{margin-bottom:4px;}p{color:#666;margin-top:0;}table{width:100%;border-collapse:collapse;margin-top:12px;}th,td{padding:6px 9px;border-bottom:1px solid #eee;text-align:left;}th{background:#f3f4f6;font-size:10px;text-transform:uppercase;}td.r,th.r{text-align:right;}</style></head><body><h2>Stock Movements</h2><p>${dateFrom || dateTo ? `Period: ${dateFrom || '…'} — ${dateTo || 'today'} · ` : ''}${filtered.length} records</p><table><thead><tr><th>Date</th><th>Product</th><th>Type</th><th>Document</th><th class="r">Change</th><th class="r">Before</th><th class="r">After</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-gray-800">{t('stockMovements', 'Stock Movements')}</h2>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg"
          >
            Export Excel
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg"
          >
            Print
          </button>
          <button
            onClick={() => fetchMovements(page)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            ↻ {t('refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search product, barcode, variant, doc#…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
          style={{ minWidth: 220 }}
        />
        <select
          value={docType}
          onChange={e => { setDocType(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
        >
          <option value="">All Types</option>
          {Object.entries(DOC_TYPE_LABELS).map(([code, info]) => (
            <option key={code} value={code}>{info.label}</option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
        />
        <input
          type="date"
          value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
        />
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-red-500 hover:text-red-700 font-medium px-2"
          >
            Clear
          </button>
        )}
      </div>

      {/* Count */}
      {!loading && !error && (
        <div className="text-xs text-gray-400 mb-3">
          {total.toLocaleString()} records
          {totalPages > 1 && ` · page ${page} of ${totalPages}`}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : error ? (
          <div className="p-10 text-center text-red-500 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">No stock movements found.</div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Date</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Product</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Document</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Change</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Before</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">After</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((m, idx) => {
                const typeInfo = DOC_TYPE_LABELS[m.document_type] || { label: m.document_type, bg: '#f3f4f6', color: '#374151' };
                const change   = m.quantity_change ?? 0;
                const isIn     = change > 0;
                const isOut    = change < 0;

                return (
                  <tr
                    key={m._id || idx}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                      {m.createdAt ? fmtLocal(m.createdAt) : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-gray-800">{m.product_name || '—'}</span>
                      {m.variant_name && (
                        <span className="ml-1.5 text-xs text-gray-400">{m.variant_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span style={{ background: typeInfo.bg, color: typeInfo.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                      {m.document_number || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">
                      <span style={{ color: isIn ? '#16a34a' : isOut ? '#dc2626' : '#6b7280' }}>
                        {isIn ? '+' : ''}{change}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-500 text-xs">
                      {m.quantity_before ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-800">
                      {m.quantity_after ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} pages={totalPages} total={total} limit={PAGE_SIZE}
        onChange={(p) => { setPage(p); fetchMovements(p); }} />
    </div>
  );
}
